/**
 * The presence beat POLICY — when a runtime should announce itself, separated
 * from HOW it reaches the callable (each runtime has its own transport: the
 * portal has the firebase SDK, natives have it too, creations' shell has only
 * `fetch` + a portal ID token).
 *
 * Three rules, and they are the whole policy:
 *
 *  - beat immediately, then every {@link PRESENCE_HEARTBEAT_MS};
 *  - only while the document is VISIBLE, and beat again the moment it becomes
 *    visible. A backgrounded tab left open overnight must decay to "last seen
 *    …" exactly like a closed one — that a browser kept it alive is not
 *    evidence its owner is around;
 *  - stop after {@link PRESENCE_IDLE_MS} without human input. A foregrounded
 *    kiosk/PWA is no stronger evidence of presence than a hidden tab. The first
 *    input after idling wakes the beat immediately.
 *
 * @djgg/creation-shell cannot import this module (it must not pull
 * @djgg/creations into a creation bundle) and carries a small mirror instead,
 * pinned to {@link PRESENCE_HEARTBEAT_MS} by its `test/contract.test.ts` — the
 * same mirror discipline it already uses for PlayModeId / RenderManifest / the
 * animation vocabulary.
 */
import { PRESENCE_HEARTBEAT_MS, PRESENCE_IDLE_MS } from './status.js';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

/** Injectable seams so the loop unit-tests without a DOM or real timers. */
export interface PresenceBeatDeps {
  intervalMs?: number;
  idleMs?: number;
  now?: () => number;
  /** Defaults to the ambient `document`; pass a stub in tests. */
  doc?: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>;
}

/**
 * Start beating presence. `send` is fired for each beat and MUST NOT throw —
 * presence is decoration and a failed beat can never surface to a player.
 * Returns the stop function; call it on teardown.
 */
export function startPresenceBeat(send: () => void, deps: PresenceBeatDeps = {}): () => void {
  const doc = deps.doc ?? (typeof document !== 'undefined' ? document : undefined);
  const now = deps.now ?? Date.now;
  const idleMs = deps.idleMs ?? PRESENCE_IDLE_MS;
  let lastActivityAt = now();
  const beat = (): void => {
    if (doc && doc.visibilityState !== 'visible') return;
    if (doc && now() - lastActivityAt > idleMs) return;
    send();
  };
  const activity = (): void => {
    const at = now();
    const wasIdle = at - lastActivityAt > idleMs;
    lastActivityAt = at;
    if (wasIdle) beat();
  };
  const visibility = (): void => {
    if (doc?.visibilityState !== 'visible') return;
    lastActivityAt = now();
    beat();
  };

  beat();
  const timer = setInterval(beat, deps.intervalMs ?? PRESENCE_HEARTBEAT_MS);
  doc?.addEventListener('visibilitychange', visibility);
  for (const event of ACTIVITY_EVENTS) doc?.addEventListener(event, activity, { passive: true });
  return () => {
    clearInterval(timer);
    doc?.removeEventListener('visibilitychange', visibility);
    for (const event of ACTIVITY_EVENTS) doc?.removeEventListener(event, activity);
  };
}
